import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import useApi from '../../hooks/useApi'
import { ensureAbsoluteUrl } from '../../utils/url'
import ProfileTemplate from './ProfileTemplate'
import { ProfileMain } from '../../components/Profiles'
import { BusinessProfileLeftSidebar } from '../../components/Profiles/BusinessProfile'
import Loading from '../../components/loading'


function buildSocialLinks(obj) {
  if (!obj) return []
  const platforms = [
    { key: 'facebook_link', platform: 'facebook' },
    { key: 'instagram_link', platform: 'instagram' },
    { key: 'linkedin_link', platform: 'linkedin' },
    { key: 'youtube_link', platform: 'youtube' },
    { key: 'tiktok_link', platform: 'tiktok' },
  ]
  return platforms
    .filter(({ key }) => obj[key])
    .map(({ platform, key }) => ({ platform, href: ensureAbsoluteUrl(obj[key]), label: platform }))
}


function formatBusinessLocation(business) {
  if (!business) return ''
  const parts = [business.city, business.state].filter(Boolean)
  return parts.join(', ')
}


export default function BusinessProfilePage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { currentUser } = useCurrentUser()
  const { getBusiness } = useApi()

  const [business, setBusiness] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const businessId = currentUser?.business_id

  useEffect(() => {
    if (!businessId) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        const { data } = await getBusiness(businessId)
        if (!cancelled && data) {
          setBusiness({ ...data, industry_id: data.industry?.id })
        }
      } catch (err) {
        if (!cancelled) setBusiness(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [businessId])

  if (isLoading) {
    return <Loading show />
  }

  if (!businessId || !business) {
    navigate('/business-account', { replace: true })
    return null
  }

  const locationStr = formatBusinessLocation(business)
  const industryName = business.industry
    ? (i18n.language === 'es' ? business.industry.es_name : business.industry.en_name) || business.industry.en_name
    : ''
  const logoUrl =
    business.media_attachment?.thumbnail_url ??
    business.media_attachment?.direct_upload_url ??
    null
  const coverImageUrl =
    business.cover_photo?.thumbnail_url ??
    business.cover_photo?.direct_upload_url ??
    undefined

  
  const isBusinessOwner = currentUser?.has_business && currentUser?.business_id === businessId

  return (
    <ProfileTemplate
      profileType="business"
      headerProps={{
        coverImageUrl,
        logoUrl,
        businessName: business.name,
        onUploadLogoClick: isBusinessOwner ? () => navigate('/business-account') : undefined,
        onEditPageClick: isBusinessOwner ? () => navigate('/business-account') : undefined,
        socialLinks: buildSocialLinks(business),
        onFollowClick: () => {},
        onShareClick: () => {},
      }}
      breadcrumbItems={[
        { label: t('profiles.breadcrumbHome', 'CR Homepage'), href: '/' },
        { label: t('profiles.breadcrumbIndustry', 'Industry') },
        { label: business.name },
      ]}
      leftSidebarContent={
        <BusinessProfileLeftSidebar
          name={business.name}
          location={locationStr}
          industryName={industryName}
          bio={business.community_statement}
          websiteUrl={business.website_url}
          followersCount={0}
          categories={business.categories}
          locale={currentUser?.language ?? i18n.language ?? 'en'}
        />
      }
      mainContent={<ProfileMain location={locationStr} />}
      rightSidebarContent={null}
    />
  )
}
